import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calculator, TrendingUp, DollarSign, Users, Clock,
  Plus, Trash2, Save, Download, AlertCircle, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';

const SROICalculatorPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [calculation, setCalculation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    directCosts: '',
    indirectCosts: '',
    beneficiaries: '',
    timeHorizon: 3,
    deadweight: 0.2,
    attribution: 0.8,
    dropOff: 0.3,
    directBenefits: [
      { type: 'income_increase', value: '', beneficiaries: '' }
    ],
    indirectBenefits: [
      { type: 'health_improvement', value: '' }
    ]
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${apiUrl}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const addDirectBenefit = () => {
    setFormData({
      ...formData,
      directBenefits: [...formData.directBenefits, { type: '', value: '', beneficiaries: '' }]
    });
  };

  const removeDirectBenefit = (index) => {
    const updated = formData.directBenefits.filter((_, i) => i !== index);
    setFormData({ ...formData, directBenefits: updated });
  };

  const addIndirectBenefit = () => {
    setFormData({
      ...formData,
      indirectBenefits: [...formData.indirectBenefits, { type: '', value: '' }]
    });
  };

  const removeIndirectBenefit = (index) => {
    const updated = formData.indirectBenefits.filter((_, i) => i !== index);
    setFormData({ ...formData, indirectBenefits: updated });
  };

  const updateDirectBenefit = (index, field, value) => {
    const updated = [...formData.directBenefits];
    updated[index][field] = value;
    setFormData({ ...formData, directBenefits: updated });
  };

  const updateIndirectBenefit = (index, field, value) => {
    const updated = [...formData.indirectBenefits];
    updated[index][field] = value;
    setFormData({ ...formData, indirectBenefits: updated });
  };

  const handleCalculate = async () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const payload = {
        projectId: selectedProject,
        directCosts: parseFloat(formData.directCosts),
        indirectCosts: parseFloat(formData.indirectCosts),
        beneficiaries: parseInt(formData.beneficiaries),
        timeHorizon: parseInt(formData.timeHorizon),
        deadweight: parseFloat(formData.deadweight),
        attribution: parseFloat(formData.attribution),
        dropOff: parseFloat(formData.dropOff),
        directBenefits: formData.directBenefits.map(b => ({
          type: b.type,
          value: parseFloat(b.value),
          beneficiaries: parseInt(b.beneficiaries)
        })),
        indirectBenefits: formData.indirectBenefits.map(b => ({
          type: b.type,
          value: parseFloat(b.value)
        }))
      };

      const res = await fetch(`${apiUrl}/api/impact/sroi/calculate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const result = await res.json();
        setCalculation(result);
        setSuccess('SROI calculation completed successfully!');
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to calculate SROI');
      }
    } catch (error) {
      console.error('Error calculating SROI:', error);
      setError('An error occurred while calculating SROI');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!calculation) return;

    const exportData = {
      project: projects.find(p => p.id === selectedProject)?.title,
      calculation: calculation,
      generatedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sroi-calculation-${selectedProject}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-teal-600 hover:text-teal-700 mb-4 flex items-center"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Calculator className="w-8 h-8 mr-3 text-teal-600" />
                SROI Calculator
              </h1>
              <p className="text-gray-600 mt-1">Calculate Social Return on Investment for your projects</p>
            </div>
          </div>
        </div>

        {error && <AlertBanner type="error" message={error} className="mb-6" />}
        {success && <AlertBanner type="success" message={success} className="mb-6" />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Input Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Project</h2>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">-- Select a Project --</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.title} ({project.projectCode})
                  </option>
                ))}
              </select>
            </div>

            {/* Investment Costs */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-teal-600" />
                Investment Costs
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Direct Costs (₹)
                  </label>
                  <input
                    type="number"
                    value={formData.directCosts}
                    onChange={(e) => setFormData({ ...formData, directCosts: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g., 5000000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Indirect Costs (₹)
                  </label>
                  <input
                    type="number"
                    value={formData.indirectCosts}
                    onChange={(e) => setFormData({ ...formData, indirectCosts: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g., 1000000"
                  />
                </div>
              </div>
            </div>

            {/* Project Parameters */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2 text-teal-600" />
                Project Parameters
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Beneficiaries
                  </label>
                  <input
                    type="number"
                    value={formData.beneficiaries}
                    onChange={(e) => setFormData({ ...formData, beneficiaries: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g., 1000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time Horizon (Years)
                  </label>
                  <input
                    type="number"
                    value={formData.timeHorizon}
                    onChange={(e) => setFormData({ ...formData, timeHorizon: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    min="1"
                    max="10"
                  />
                </div>
              </div>
            </div>

            {/* Adjustment Factors */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Adjustment Factors</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deadweight (0-1)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={formData.deadweight}
                    onChange={(e) => setFormData({ ...formData, deadweight: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">What would have happened anyway</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attribution (0-1)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={formData.attribution}
                    onChange={(e) => setFormData({ ...formData, attribution: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Credit to this project</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Drop-off (0-1)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={formData.dropOff}
                    onChange={(e) => setFormData({ ...formData, dropOff: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Annual decline in impact</p>
                </div>
              </div>
            </div>

            {/* Direct Benefits */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Direct Benefits</h2>
                <Button
                  onClick={addDirectBenefit}
                  variant="outline"
                  size="sm"
                  className="text-teal-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Benefit
                </Button>
              </div>
              <div className="space-y-4">
                {formData.directBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input
                        type="text"
                        placeholder="Benefit Type (e.g., income_increase)"
                        value={benefit.type}
                        onChange={(e) => updateDirectBenefit(index, 'type', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                      <input
                        type="number"
                        placeholder="Value (₹)"
                        value={benefit.value}
                        onChange={(e) => updateDirectBenefit(index, 'value', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                      <input
                        type="number"
                        placeholder="Beneficiaries"
                        value={benefit.beneficiaries}
                        onChange={(e) => updateDirectBenefit(index, 'beneficiaries', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    {formData.directBenefits.length > 1 && (
                      <button
                        onClick={() => removeDirectBenefit(index)}
                        className="text-red-600 hover:text-red-700 p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Indirect Benefits */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Indirect Benefits</h2>
                <Button
                  onClick={addIndirectBenefit}
                  variant="outline"
                  size="sm"
                  className="text-teal-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Benefit
                </Button>
              </div>
              <div className="space-y-4">
                {formData.indirectBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Benefit Type (e.g., health_improvement)"
                        value={benefit.type}
                        onChange={(e) => updateIndirectBenefit(index, 'type', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                      <input
                        type="number"
                        placeholder="Value (₹)"
                        value={benefit.value}
                        onChange={(e) => updateIndirectBenefit(index, 'value', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    {formData.indirectBenefits.length > 1 && (
                      <button
                        onClick={() => removeIndirectBenefit(index)}
                        className="text-red-600 hover:text-red-700 p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                onClick={handleCalculate}
                disabled={loading || !selectedProject}
                className="flex-1 text-white"
                style={{ background: '#14B8A6' }}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Calculator className="w-5 h-5 mr-2" />
                    Calculate SROI
                  </>
                )}
              </Button>
              {calculation && (
                <Button
                  onClick={handleExport}
                  variant="outline"
                  className="text-teal-600 border-teal-600"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Export Results
                </Button>
              )}
            </div>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">SROI Results</h2>

              {!calculation ? (
                <div className="text-center py-12">
                  <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">
                    Fill in the form and click "Calculate SROI" to see results
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* SROI Ratio */}
                  <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg">
                    <p className="text-sm text-teal-700 font-medium mb-2">SROI Ratio</p>
                    <p className="text-4xl font-bold text-teal-900">
                      {calculation.sroi_ratio?.toFixed(2) || 'N/A'}:1
                    </p>
                    <p className="text-sm text-teal-700 mt-2">
                      For every ₹1 invested, ₹{calculation.sroi_ratio?.toFixed(2)} of social value is created
                    </p>
                  </div>

                  {/* Breakdown */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">Total Investment</span>
                      <span className="font-semibold text-gray-900">
                        ₹{calculation.total_investment?.toLocaleString('en-IN') || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">Present Value of Benefits</span>
                      <span className="font-semibold text-gray-900">
                        ₹{calculation.total_benefits_pv?.toLocaleString('en-IN') || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">Net Present Value</span>
                      <span className="font-semibold text-teal-600">
                        ₹{calculation.npv?.toLocaleString('en-IN') || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">Total Beneficiaries</span>
                      <span className="font-semibold text-gray-900">
                        {calculation.beneficiaries?.toLocaleString('en-IN') || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-gray-600">Value per Beneficiary</span>
                      <span className="font-semibold text-gray-900">
                        ₹{calculation.value_per_beneficiary?.toLocaleString('en-IN') || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Interpretation */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900 mb-1">Interpretation</p>
                        <p className="text-xs text-blue-700">
                          {calculation.sroi_ratio >= 3
                            ? 'Excellent social value creation! This project generates significant returns.'
                            : calculation.sroi_ratio >= 1.5
                            ? 'Good social impact. The project is creating meaningful value.'
                            : 'Moderate impact. Consider optimizing implementation for better returns.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {calculation.breakdown && (
                    <div className="mt-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Benefit Breakdown</h3>
                      <div className="space-y-2">
                        <div className="text-xs">
                          <span className="text-gray-600">Direct Benefits: </span>
                          <span className="font-semibold">₹{calculation.breakdown.direct_benefits_pv?.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-600">Indirect Benefits: </span>
                          <span className="font-semibold">₹{calculation.breakdown.indirect_benefits_pv?.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SROICalculatorPage;
